// Parses Typesense category facet keys into a parent → child tree.
//
// Typesense returns BOTH flat and hierarchical labels for the same lot:
//   "Basketball", "Sports", "Sports >> Basketball", "Sports >> Tickets & Events"
//
// Per backend team: ignore the flat entries (no `>>`) entirely. Only
// hierarchical entries (`Parent >> Child`) drive the displayed tree.
// The parent label comes from the prefix; children are the suffix.
//
// Children are the only selectable rows. A child's identity (staged-map
// key + URL value) is the FULL hierarchical key ("Sports >> Memorabilia"),
// not just the short name — different parents can share a child name (e.g.
// "Memorabilia" under Sports, Music, and Entertainment), and collapsing to
// the short name made selecting one select/filter all of them. The backend
// exact-matches the full pair against the lot's `categories` facet.
//
// Returns: [{ name: parentLabel, count: null, children: [{ name: fullKey, label: shortName, count }] }]

const SEP = / *>> */

export function shortName(fullKey) {
  if (!fullKey) return null
  const parts = fullKey.split(SEP).filter(Boolean)
  return parts.length > 1 ? parts.slice(1).join(' › ') : null
}

// Helper for callers that hold a key->bool map (e.g. `filters.departments.locked`)
// and just need the truthy subset as a Set-like map.
export function normalizeHierMap(map) {
  const out = {}
  for (const k of Object.keys(map || {})) {
    if (map[k]) out[k] = true
  }
  return out
}

export default function buildCategoryTree(facets, locked = {}) {
  const byParent = new Map()

  const add = (fullKey, count) => {
    const parts = fullKey.split(SEP).filter(Boolean)
    if (parts.length < 2) return // flat entries are intentionally ignored
    const parentLabel = parts[0]
    const childShort = parts.slice(1).join(' › ')
    if (!byParent.has(parentLabel)) {
      byParent.set(parentLabel, { name: parentLabel, count: null, children: [] })
    }
    byParent.get(parentLabel).children.push({
      name: fullKey,      // staged-map key + URL value — unique per parent
      label: childShort,  // visible label inside the row
      count,
    })
  }

  for (const key of Object.keys(facets || {})) {
    add(key, facets[key])
  }
  // Surface any locked entries that the facet payload didn't include.
  for (const key of Object.keys(locked || {})) {
    if (locked[key] && (facets || {})[key] == null) {
      add(key, null)
    }
  }

  return Array.from(byParent.values())
}
